import React from 'react'
import Head from 'next/head'
import Pitch from '@src/components/DisposalPage/Pitch'
import FeatureList from '@src/components/DisposalPage/FeatureList'
import Approach from '@src/components/DisposalPage/Approach'
import CaseStudy from '@src/components/DisposalPage/CaseStudy'
import FAQ from '@src/components/DisposalPage/FAQ'
import SustainabilityDashboardPromo from '@src/components/DisposalPage/SustainabilityDashboardPromo'
import SchemaMarkup from '@src/components/SchemaMarkup'
import RelatedServices from '@src/components/RelatedServices'
import Clients from '@src/pages/LandingPage/Clients'
import TrustBox from '@src/pages/common/components/TrustBox'
import TrustedByClients from '@src/components/DisposalPage/TrustedByClients'
import pageClasses from '@src/styles/hand-sanitizer-recycling-page.module.css'
// eslint-disable-next-line import/extensions
import sanitizerRecycling from '../../json/hand-sanitizer-recycling-data.json'

const sanitizerServiceData = {
  name: 'Hand Sanitizer Recycling',
  description:
    'Bulk hand sanitizer recycling through ethanol reclamation and donation for businesses, government, and institutions. Dimension recovers alcohol from expired sanitizer with full EPA/RCRA-compliant documentation.',
  serviceType: 'Hand Sanitizer Recycling',
  url: 'https://getdimension.com/hand-sanitizer-recycling',
  provider: {
    name: 'Dimension',
    url: 'https://getdimension.com',
    address: {
      streetAddress: '2261 Market Street, #4061',
      addressLocality: 'San Francisco',
      addressRegion: 'CA',
      postalCode: '94114',
      addressCountry: 'US',
    },
    telephone: '+1 (415) 340-7182',
  },
  offers: [
    { name: 'Ethanol Reclamation and Recycling' },
    { name: 'Expired Hand Sanitizer Recycling' },
    { name: 'Surplus Sanitizer Donation' },
    { name: 'Bulk Pallet Sanitizer Pickup' },
    { name: 'Waste Diversion Reporting' },
  ],
}

const sanitizerFAQData = {
  questions: sanitizerRecycling.FAQContent.contentList.map((faq) => ({
    question: faq.heading.replace(/<[^>]*>/g, ''),
    answer: faq.textList.join(' '),
  })),
}

function HandSanitizerRecycling() {
  return (
    <>
      <Head>
        <title>Hand Sanitizer Recycling &amp; Ethanol Reclamation | Dimension</title>
        <meta
          name="description"
          content="Recycle bulk expired hand sanitizer through ethanol reclamation and donation. Dimension recovers alcohol from surplus sanitizer, EPA/RCRA-compliant, from around $50 per pallet. Get an instant quote."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://getdimension.com/hand-sanitizer-recycling" />
        <SchemaMarkup type="WebSite" />
        <SchemaMarkup type="Service" data={sanitizerServiceData} />
        <SchemaMarkup type="FAQPage" data={sanitizerFAQData} />
        <SchemaMarkup type="LocalBusiness" />
      </Head>
      <div className={pageClasses.root}>
        <Pitch
          variant="hazardous-waste"
          heroImage="hand-sanitizer"
          heading={sanitizerRecycling.pitch.heading}
          material={sanitizerRecycling.pitch.material}
          pitchText={sanitizerRecycling.pitch.pitchText}
          ctaLink={sanitizerRecycling.pitch.ctaLink}
          ctaText={sanitizerRecycling.pitch.ctaText}
          belowCta={<TrustBox isMobile isDark={false} />}
          belowPitch={<TrustedByClients />}
        />
        <Clients />
        <FeatureList {...sanitizerRecycling.solutions} />
        <SustainabilityDashboardPromo />
        <FeatureList {...sanitizerRecycling.benefits} />
        <CaseStudy {...sanitizerRecycling.caseStudy} />
        <Approach {...sanitizerRecycling.approach} />
        <FAQ {...sanitizerRecycling.FAQContent} />
        <RelatedServices
          services={[
            {
              label: 'Hand Sanitizer Disposal',
              href: '/hand-sanitizer-disposal',
              description:
                'Compliant bulk disposal of expired, surplus, and recalled hand sanitizer for businesses.',
            },
            {
              label: 'PPE Disposal and Recycling',
              href: '/ppe-disposal',
              description:
                'Compliant disposal of masks, gloves, gowns, and other expired or surplus PPE for businesses.',
            },
            {
              label: 'Sustainability',
              href: '/sustainability',
              description:
                'Learn how Dimension diverts waste from landfills through recycling and responsible disposal.',
            },
            {
              label: 'Hazardous Waste Disposal',
              href: '/hazardous-waste-disposal',
              description:
                'Safe disposal of chemical waste, solvents, and other hazardous materials for businesses.',
            },
          ]}
        />
      </div>
    </>
  )
}

export default HandSanitizerRecycling
